import React, { createContext, useContext, useState } from "react";
import { ParlaySelection } from "../models/MyBetModel.tsx";
import {
    getMarketConflictGroup,
    resolveParlayQueeAction,
} from "../helpers/parlayMarketConflicts.ts";
import { syncTeaserOddName } from "../helpers/functions.tsx";

interface IParlayBetContext {
    bets: ParlaySelection[],
    updateQuee: Function,
    deleteBet: Function,
    setBets: React.Dispatch<React.SetStateAction<any>>;
    deleteAllBets: Function,
    teaserPoints: Number
    setTeaserPoints: React.Dispatch<React.SetStateAction<any>>;
    teaserType: null,
    setTeaserType: React.Dispatch<React.SetStateAction<any>>;
    teaserFixture: string,
    setTeaserFixture: React.Dispatch<React.SetStateAction<any>>;
    updateBetTeaser: Function,
    updateExistingBet: Function,
    errorMessageBets: string,
    setErrorMessageBets: React.Dispatch<React.SetStateAction<any>>;
}

const ParlayBetContext = createContext<IParlayBetContext | undefined>(undefined);

interface ParlayBetProviderProps {
    children: React.ReactNode;
}

export const ParlayBetProvider: React.FC<ParlayBetProviderProps> = ({ children }) => {
    // State for the context
    const [bets, setBets] = useState<ParlaySelection[]>([]);
    const [teaserPoints, setTeaserPoints] = useState(Number);
    const [teaserType, setTeaserType] = useState(null);
    const [teaserFixture, setTeaserFixture] = useState('');
    const [errorMessageBets, setErrorMessageBets] = useState('');

    const updateQuee = (bet: any) => {
        if (!bet?.odd?.market_id) return;

        setBets(prevBets => {
            const decision = resolveParlayQueeAction(prevBets, bet);

            if (decision.action === 'toggle_off') {
                return prevBets.filter(e => e.id_selection !== bet.id_selection);
            }

            if (decision.action === 'reject') {
                return prevBets;
            }

            let next = [...prevBets];
            if (decision.action === 'replace' && decision.removeSelectionId) {
                next = next.filter(e => e.id_selection !== decision.removeSelectionId);
            }

            next.push(bet);
            return next;
        });
    };

    const updateExistingBet = (updatedBet: ParlaySelection) => {
        setBets(prev =>
            prev.map(b => {
                if (b.gameId !== updatedBet.gameId) return b;

                const existingGroup = getMarketConflictGroup(b.odd?.market_id);
                const updatedGroup = getMarketConflictGroup(updatedBet.odd?.market_id);

                if (existingGroup && updatedGroup) {
                    return existingGroup === updatedGroup ? updatedBet : b;
                }

                // Special / props: match by market_id
                return b.odd?.market_id === updatedBet.odd?.market_id ? updatedBet : b;
            })
        );
    };

    const updateBetTeaser = (bet: ParlaySelection) => {
        if (!bet || !bet.marketName) return;

        const odd = bet.odd ? syncTeaserOddName(bet.odd) : bet.odd;
        const nextBet = {
            ...bet,
            odd,
            betName: odd?.name ?? bet.betName,
        };

        const updatedQuee = [...bets];

        const betQueeIndex = updatedQuee.findIndex(existingBet =>
            existingBet.id_selection === nextBet.id_selection &&
            existingBet.teamId === nextBet.teamId &&
            existingBet.marketName === nextBet.marketName
        );

        if (betQueeIndex === -1) {
            updatedQuee.push(nextBet);
        } else {
            updatedQuee[betQueeIndex] = { ...nextBet };
        }

        setBets(updatedQuee);
    };

    const deleteBet = (betToRemove: ParlaySelection) => {
        setBets((prevBets) => {
            const newBets = prevBets.filter(bet => {
                const isSameBet = bet.odd?.id === betToRemove.odd?.id;
                return !isSameBet;
            });
            return newBets;
        });
    };

    const deleteAllBets = () => {
        setBets([]);
    };

    const value = {
        bets, setBets, updateQuee, deleteBet, deleteAllBets, updateBetTeaser, teaserPoints, setTeaserPoints, teaserType, setTeaserType,
        teaserFixture, setTeaserFixture, updateExistingBet, errorMessageBets, setErrorMessageBets
    };

    return <ParlayBetContext.Provider value={value}>
        {children}
    </ParlayBetContext.Provider>;
};

export const useBetContext = (): IParlayBetContext => {

    const context = useContext(ParlayBetContext);

    if (!context) {
        throw new Error("useBetContext must be used within a BetProvider");
    }

    return context;
};
